import React, { useEffect, useRef, useState } from "react";
import { QrCode, CheckCircle2, Clock, RefreshCw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { Button, Modal } from "@/components/common";

function mmss(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/** Shows a Midtrans QRIS QR code for an order with a live expiry countdown, and polls
 *  payment status until the customer pays (auto-closes) or the QR expires. */
export default function QrisPaymentModal({ order, method = "qris", onPaid, onClose }) {
  const [qr, setQr] = useState(null);
  const [left, setLeft] = useState(0);
  const [status, setStatus] = useState("loading"); // loading | pending | paid | expired | error
  const [err, setErr] = useState(null);
  const [checking, setChecking] = useState(false);
  const pollRef = useRef(null);

  const create = async () => {
    setStatus("loading");
    setErr(null);
    try {
      const d = await api.post("/payments/qris", { order_id: order.id, method });
      setQr(d);
      setLeft(d.expires_in_seconds ?? 0);
      setStatus(d.paid ? "paid" : "pending");
      if (d.paid) onPaid?.();
    } catch (e) {
      setErr(e?.data?.detail || e.message || "Gagal membuat QRIS");
      setStatus("error");
    }
  };

  useEffect(() => { create();   }, []);

  // Countdown tick
  useEffect(() => {
    if (status !== "pending") return;
    const t = setInterval(() => setLeft((v) => {
      if (v <= 1) { setStatus("expired"); return 0; }
      return v - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [status]);

  // Poll payment status
  useEffect(() => {
    if (status !== "pending") { clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(async () => {
      try {
        const s = await api.get(`/payments/qris/${order.id}/status`);
        if (s.paid) { setStatus("paid"); clearInterval(pollRef.current); onPaid?.(); }
        else if (["expire", "deny", "cancel", "failure"].includes(s.status)) setStatus("expired");
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [status, order.id]);  

  const checkNow = async () => {
    setChecking(true);
    try {
      const s = await api.get(`/payments/qris/${order.id}/status`);
      if (s.paid) { setStatus("paid"); onPaid?.(); }
      else if (["expire", "deny", "cancel", "failure"].includes(s.status)) setStatus("expired");
    } finally { setChecking(false); }
  };

  return (
    <Modal open onClose={onClose} title="Pembayaran QRIS" maxWidth="max-w-sm">
      <div className="flex flex-col items-center text-center" data-testid="qris-modal">
        <div className="text-sm text-slate-500">Pesanan {order.order_no}</div>
        <div className="text-2xl font-bold text-slate-800 mt-0.5 mb-3" data-testid="qris-amount">{rupiah(qr?.amount ?? order.total)}</div>

        {status === "loading" && (
          <div className="py-16 flex flex-col items-center gap-2 text-slate-500" data-testid="qris-loading">
            <Loader2 className="animate-spin" size={28} /> <span className="text-sm">Membuat kode QRIS…</span>
          </div>
        )}

        {status === "error" && (
          <div className="py-10 flex flex-col items-center gap-3">
            <div className="text-sm text-rose-600" data-testid="qris-error">{String(err)}</div>
            <Button variant="subtle" onClick={create}><RefreshCw size={15} /> Coba Lagi</Button>
          </div>
        )}

        {(status === "pending" || status === "expired") && qr?.qr_image_url && (
          <>
            <div className={`relative rounded-2xl border-2 p-3 bg-white ${status === "expired" ? "border-slate-200 opacity-40" : "border-primary/30"}`}>
              <img src={qr.qr_image_url} alt="QRIS" className="w-56 h-56 object-contain" data-testid="qris-image" />
              {status === "expired" && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="bg-slate-800 text-white text-xs font-semibold px-3 py-1 rounded-full">Kedaluwarsa</span>
                </div>
              )}
            </div>
            {status === "pending" ? (
              <>
                <div className="flex items-center gap-1.5 text-amber-600 font-semibold mt-3" data-testid="qris-countdown">
                  <Clock size={16} /> Sisa waktu {mmss(left)}
                </div>
                <p className="text-xs text-slate-400 mt-1 px-4">Scan dengan GoPay, OVO, DANA, ShopeePay, atau m-banking apa pun. Status akan terupdate otomatis setelah pembayaran.</p>
                <Button className="w-full mt-4" variant="subtle" onClick={checkNow} disabled={checking} data-testid="qris-check">
                  {checking ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />} Cek Status Pembayaran
                </Button>
              </>
            ) : (
              <>
                <div className="text-sm text-slate-500 mt-3">Kode QRIS sudah kedaluwarsa.</div>
                <Button className="w-full mt-3" onClick={create} data-testid="qris-regenerate"><RefreshCw size={15} /> Buat QR Baru</Button>
              </>
            )}
            <button onClick={onClose} className="mt-3 text-sm text-slate-500 hover:text-slate-700" data-testid="qris-close">Bayar nanti / Tutup</button>
          </>
        )}

        {status === "paid" && (
          <div className="py-12 flex flex-col items-center gap-3" data-testid="qris-paid">
            <CheckCircle2 className="text-emerald-500" size={56} />
            <div className="text-lg font-semibold text-slate-800">Pembayaran Berhasil</div>
            <div className="text-sm text-slate-500">Pesanan {order.order_no} telah LUNAS.</div>
            <Button className="w-full mt-2" onClick={() => onPaid?.()} data-testid="qris-done">Selesai</Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
